import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'

// Prevent infinite reload loops
let reloadAttempts = 0;
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_COOLDOWN = 5000; // 5 seconds
let lastReloadTime = 0;

const canAttemptReload = () => {
  const now = Date.now();
  if (now - lastReloadTime < RELOAD_COOLDOWN) {
    return false;
  }
  if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
    return false;
  }
  return true;
};

const safeReload = () => {
  if (canAttemptReload()) {
    reloadAttempts++;
    lastReloadTime = Date.now();
    window.location.reload();
  } else {
    console.error('Auto-reload prevented to stop infinite loop');
  }
};

// Enhanced error handling for blank screen prevention
const handleGlobalError = (error: Error, context: string) => {
  console.error(`🔴 ${context}:`, error);
  
  // Show a fallback UI instead of blank screen
  const showFallbackUI = (errorMessage: string) => {
    document.body.innerHTML = `
      <div style="
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        color: white;
        padding: 20px;
      ">
        <div style="
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 40px;
          text-align: center;
          max-width: 500px;
          width: 100%;
        ">
          <div style="
            width: 64px;
            height: 64px;
            background: #3B82F6;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 24px;
          ">
            <div style="
              width: 32px;
              height: 32px;
              border: 3px solid white;
              border-top: 3px solid transparent;
              border-radius: 50%;
              animation: spin 1s linear infinite;
            "></div>
          </div>
          
          <h1 style="
            font-size: 28px;
            font-weight: bold;
            margin: 0 0 16px 0;
            background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          ">PromptOS</h1>
          
          <h2 style="font-size: 20px; margin: 0 0 12px 0; color: #FEF3C7;">
            Loading Issue
          </h2>
          
          <p style="margin: 0 0 24px 0; color: rgba(255, 255, 255, 0.8); line-height: 1.5;">
            ${errorMessage}
          </p>
          
          <button onclick="window.location.reload()" style="
            background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-right: 12px;
          " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'" onclick="if(typeof safeReload === 'function') safeReload(); else window.location.reload();">
            🔄 Refresh Page
          </button>
          
          <button onclick="localStorage.clear(); sessionStorage.clear(); if(typeof safeReload === 'function') safeReload(); else window.location.reload();" style="
            background: transparent;
            color: white;
            border: 1px solid rgba(255, 255, 255, 0.3);
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
          " onmouseover="this.style.background='rgba(255, 255, 255, 0.1)'" onmouseout="this.style.background='transparent'">
            🧹 Clear Cache & Refresh
          </button>
        </div>
        
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      </div>
    `;
  };
  
  // Show appropriate fallback based on error type
  if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
    showFallbackUI('Unable to connect to PromptOS servers. Please check your internet connection and try refreshing the page.');
  } else if (error.message?.includes('auth') || error.message?.includes('session')) {
    showFallbackUI('Authentication system is having issues. Try clearing your browser cache and refreshing the page.');
  } else {
    showFallbackUI('PromptOS encountered an unexpected error. Please refresh the page to continue.');
  }
};

// Enhanced global error handlers
window.addEventListener('error', (event) => {
  handleGlobalError(event.error || new Error('Unknown error'), 'Window Error');
});

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  handleGlobalError(error, 'Unhandled Promise Rejection');
});

try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }
  
  // Debug environment variables immediately
  console.log('🔍 ENVIRONMENT DEBUG AT STARTUP:');
  console.log('Mode:', import.meta.env.MODE);
  console.log('All env vars:', import.meta.env);
  console.log('VITE_OPENAI_API_KEY:', import.meta.env.VITE_OPENAI_API_KEY ? 'EXISTS' : 'MISSING');
  if (import.meta.env.VITE_OPENAI_API_KEY) {
    console.log('Key preview:', import.meta.env.VITE_OPENAI_API_KEY.substring(0, 7) + '...');
  }

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
} catch (error) {
  const renderError = error instanceof Error ? error : new Error('Failed to render app');
  handleGlobalError(renderError, 'App Render Error');
}